import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, HeartHandshake, LifeBuoy, Heart, Check, Upload } from "lucide-react";
import { SectionHead, Reveal } from "@/components/Reveal";
import { Sparkles as ShimmerSparkles, ShimmerCard } from "@/components/Shimmer";
import { GIRL_CODE_LOGO_URL } from "@/components/GirlCodeLogo";
import { useCashApp } from "@/components/CashAppModal";
import { api, mediaUrl } from "@/lib/api";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1619497442016-fb99b15c549d?crop=entropy&cs=srgb&fm=jpg&q=85";
const CHEER_IMG =
  "https://images.unsplash.com/photo-1579487900873-f9b2e615896e?crop=entropy&cs=srgb&fm=jpg&q=85";
const MENTOR_IMG =
  "https://images.pexels.com/photos/12887163/pexels-photo-12887163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SKILLS_IMG =
  "https://images.pexels.com/photos/37138035/pexels-photo-37138035.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CENTER_OFFERINGS = [
  "After-school programming",
  "Tutoring and academic support",
  "Mentorship",
  "Leadership development",
  "Girl Code Project STL",
  "Sports and recreation",
  "Cheer and dance",
  "Life-skills education",
  "Career and entrepreneurship exposure",
  "Safe social and community spaces",
  "Summer programming",
];

const MediaStrip = ({ label }) => (
  <div
    data-testid="media-frame"
    className="mt-5 rounded-2xl border-2 border-dashed border-purple-200 bg-lavender-50 px-5 py-4 flex items-center gap-3"
  >
    <Upload className="w-4 h-4 text-purple-600 shrink-0" />
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">{label}</p>
  </div>
);

const Section = ({ icon: Icon, title, tagline, copy, points, reverse = false, accent = false, accentText = "Girls' program — shine on", chips = null, testid, children }) => (
  <div className="py-14 sm:py-20" data-testid={testid}>
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div className={reverse ? "lg:order-2" : ""}>
        <Reveal>
          <div className="w-12 h-12 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center">
            <Icon className="w-5 h-5 text-purple-700" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-6">{title}</h2>
          {tagline && <p className="text-fuchsia-600 text-base font-bold mt-3">{tagline}</p>}
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-4">{copy}</p>
          {chips && (
            <div className="flex flex-wrap gap-2 mt-6" data-testid="girl-code-chips">
              {chips.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-fuchsia-300 bg-fuchsia-50 px-3.5 py-1.5 text-xs font-bold text-fuchsia-700"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
          {points && (
            <ul className="mt-7 space-y-3">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-700 text-sm sm:text-base">
                  <Check className="w-4 h-4 mt-1 text-purple-600 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          )}
          {accent && (
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fuchsia-600 mt-7">
              {accentText}
            </p>
          )}
        </Reveal>
      </div>
      <div className={reverse ? "lg:order-1" : ""}>{children}</div>
    </div>
  </div>
);

const Programs = () => {
  const { open } = useCashApp();
  const [realPhotos, setRealPhotos] = useState([]);

  useEffect(() => {
    api
      .get("/media")
      .then((r) => setRealPhotos(r.data.filter((p) => p.media_type === "photo")))
      .catch(() => setRealPhotos([]));
  }, []);

  // Newest published photo goes to the homepage hero; the previous one (if any)
  // dresses the BDT Cheer section. Falls back to the placeholder shot.
  const cheerPhoto = realPhotos.length >= 2 ? mediaUrl(realPhotos[1].storage_path) : CHEER_IMG;
  return (
    <main className="pt-32">
      <section className="max-w-7xl mx-auto px-6 pb-8 bg-gradient-to-b from-lavender-100 to-transparent rounded-b-3xl pt-8 -mt-8" data-testid="programs-hero">
        <SectionHead
          eyebrow="Programs"
          title="There's more than one way to discover who you're meant to be."
        />
        <Reveal delay={0.1}>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-purple-700 mt-6">
            Sports. Sisterhood. Leadership. Money. Confidence. School. Life.
          </p>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-4 max-w-2xl">
            Y.E.S. gives young people opportunities to explore it all — while surrounded by adults
            who believe in them.
          </p>
        </Reveal>
      </section>

      <div className="max-w-7xl mx-auto px-6 divide-y divide-purple-100 border-t border-purple-100 mt-8">
        <Section
          testid="program-bdt-cheer"
          icon={Sparkles}
          title="BDT Cheer"
          tagline="Bring the energy. Build the confidence."
          copy="More than sidelines and performances. Cheer develops teamwork, discipline, confidence, commitment, leadership, and community."
          points={["Teamwork, discipline, and commitment", "Confidence built through performance", "A community that shows up for each other"]}
          accent
          accentText="Girls' program — shine on"
        >
          <ShimmerCard className="rounded-2xl">
            <div className="relative">
              <ShimmerSparkles count={12} />
              <img
                src={cheerPhoto}
                alt="BDT Cheer squad posing together in their black uniforms and bows"
                loading="lazy"
                className="w-full aspect-[4/3] object-cover rounded-2xl"
                data-testid="cheer-photo"
              />
            </div>
          </ShimmerCard>
          <MediaStrip label="Real BDT Cheer photos & videos appear here once uploaded and approved" />
        </Section>

        <Section
          testid="program-girl-code"
          icon={HeartHandshake}
          title="Girl Code Project STL"
          tagline="Real conversations. Real sisterhood. Real growth."
          copy="A space for girls ages 10–17 to talk about the things that actually matter — confidence, friendships, money, goals, hygiene, leadership, entrepreneurship, self-love, etiquette, and becoming the young woman YOU want to be."
          chips={["Mentorship", "Sisterhood", "Confidence", "Leadership", "Ages 10–17"]}
          accent
          accentText="Empower. Encourage. Elevate. We rise together."
          reverse
        >
          <ShimmerCard className="rounded-2xl">
            <div className="relative">
              <ShimmerSparkles count={12} />
              <div className="p-6 sm:p-8" data-testid="girl-code-logo-panel">
                <div className="bg-white ring-1 ring-purple-200 rounded-2xl p-5 max-w-[260px] mx-auto shadow-xl shadow-purple-200/70 -rotate-2">
                  <img
                    src={GIRL_CODE_LOGO_URL}
                    alt="Girl Code Project STL logo — Empower. Encourage. Elevate. We rise together."
                    className="w-full object-contain"
                  />
                </div>
              </div>
              <div className="px-6 sm:px-8 pb-6 sm:pb-8">
                <div
                  data-testid="media-frame"
                  className="rounded-2xl border-2 border-dashed border-purple-200 bg-lavender-50 px-5 py-4 flex items-center gap-3"
                >
                  <Upload className="w-4 h-4 text-purple-600 shrink-0" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                    Real Girl Code photos & videos appear here once uploaded and approved
                  </p>
                </div>
              </div>
            </div>
          </ShimmerCard>
        </Section>

        <Section
          testid="program-leadership"
          icon={HeartHandshake}
          title="Leadership & Mentorship"
          tagline="Your voice matters. Learn how to use it."
          copy="Mentors, workshops, and experiences help young people communicate confidently, set goals, solve problems, advocate for themselves, lead others, and prepare for what's next."
          points={["Confident communication and self-advocacy", "Goal-setting and problem-solving", "Leadership experience that opens doors"]}
        >
          <img
            src={MENTOR_IMG}
            alt="A mentor talking with a teenager in a sunny park"
            loading="lazy"
            className="w-full aspect-[4/3] object-cover rounded-3xl shadow-xl shadow-purple-200/60 ring-1 ring-purple-100"
            data-testid="leadership-photo"
          />
          <MediaStrip label="Real Leadership & Mentorship photos appear here once uploaded and approved" />
        </Section>

        <Section
          testid="program-life-skills"
          icon={LifeBuoy}
          title="Life Skills"
          tagline="Because there's a lot about life nobody teaches you in class."
          copy="Financial literacy, credit, digital safety, self-care, etiquette, career preparation, healthy relationships, communication, and other practical skills young people can carry into adulthood."
          points={["Money, credit, and financial literacy", "Digital safety and self-care", "Careers, communication, and healthy relationships"]}
          reverse
        >
          <img
            src={SKILLS_IMG}
            alt="Young culinary students practicing in a kitchen"
            loading="lazy"
            className="w-full aspect-[4/3] object-cover rounded-3xl shadow-xl shadow-purple-200/60 ring-1 ring-purple-100"
            data-testid="skills-photo"
          />
          <MediaStrip label="Real Life Skills photos appear here once uploaded and approved" />
        </Section>
      </div>

      {/* Flexible growth strip */}
      <section className="py-16" data-testid="programs-more-coming">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="rounded-2xl border-2 border-dashed border-purple-300 bg-lavender-50 p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-700">Built to grow</p>
                <p className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-2">
                  Additional sports, academic, and enrichment programs are on the way.
                </p>
                <p className="text-slate-500 text-sm mt-2">
                  Y.E.S. is designed to add new opportunities as the community needs them.
                </p>
              </div>
              <Link to="/#contact" data-testid="program-idea-btn" className="btn-outline px-6 py-3 text-sm shrink-0">
                Suggest a Program <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Where We're Going */}
      <section className="py-20 sm:py-24 bg-lavender-50 border-t border-purple-100" data-testid="programs-youth-center">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden border border-purple-100 shadow-xl shadow-purple-200/50">
              <img src={ARCH_IMG} alt="St. Louis skyline and Gateway Arch at dusk" loading="lazy" className="w-full h-72 sm:h-96 object-cover" />
            </div>
            <div className="relative bg-white rounded-3xl border border-purple-100 shadow-xl shadow-purple-200/50 p-8 sm:p-12 mx-4 sm:mx-10 -mt-20 relative z-10">
              <p className="eyebrow">Where We&apos;re Going · Not Yet Open</p>
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-ink mt-3 max-w-2xl leading-tight">
                The future Y.E.S. Youth Center — North St. Louis County
              </h2>
              <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
                We&apos;re working toward a dedicated youth-centered space where young people can
                access after-school programming, tutoring, mentorship, leadership development, Girl
                Code Project STL, sports and recreation, cheer and dance, life-skills education,
                career and entrepreneurship exposure, safe community spaces, and summer
                programming — all in one place.
              </p>
              <p className="text-purple-800 font-bold mt-4">
                The partners who join now become founding partners in this vision.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/#contact" data-testid="youth-center-partner-btn" className="btn-primary px-6 py-3 text-sm">
                  Help Build What&apos;s Next <ArrowRight className="w-4 h-4" />
                </Link>
                <button onClick={open} data-testid="youth-center-give-btn" className="btn-outline px-6 py-3 text-sm">
                  <Heart className="w-4 h-4" /> Invest in Our Youth
                </button>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8 sm:mx-10">
            {CENTER_OFFERINGS.map((item) => (
              <div key={item} className="flex items-start gap-2.5 rounded-xl bg-white border border-purple-100 px-4 py-3.5 shadow-sm">
                <Check className="w-4 h-4 mt-0.5 text-purple-600 shrink-0" />
                <span className="text-slate-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Programs;